import { Plugin, PluginKey } from "@tiptap/pm/state"
import { mergeAttributes, Node, nodeInputRule, ReactNodeViewRenderer } from "@tiptap/react"

import type { Transaction } from "@tiptap/pm/state"

import { CitationNodeView } from "./citation-node-view"

const CITATION_INPUT_REGEX = /\[\[([^\]]+)\]\]$/

export type CitationAttributes = {
  citation: string
  index: number
}

const citationPluginKey = new PluginKey("citationIndexer")

function recomputeCitationIndices(tr: Transaction): Transaction {
  const seenCitations = new Map<string, number>()
  let nextIndex = 0

  tr.doc.descendants((node, pos) => {
    if (node.type.name === "canvasCitation") {
      const citation = node.attrs.citation as string
      let assignedIndex: number

      if (seenCitations.has(citation)) {
        assignedIndex = seenCitations.get(citation)!
      } else {
        assignedIndex = nextIndex
        seenCitations.set(citation, assignedIndex)
        nextIndex++
      }

      if (node.attrs.index !== assignedIndex) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, index: assignedIndex })
      }
    }
    return true
  })

  return tr
}

function documentHasCitations(doc: Transaction["doc"]): boolean {
  let found = false
  doc.descendants((node) => {
    if (node.type.name === "canvasCitation") {
      found = true
      return false
    }
    return !found
  })
  return found
}

function createCitationIndexerPlugin() {
  return new Plugin({
    key: citationPluginKey,
    appendTransaction(_transactions, oldState, newState) {
      if (oldState.doc.eq(newState.doc)) {
        return null
      }

      if (!documentHasCitations(newState.doc)) {
        return null
      }

      const tr = newState.tr
      recomputeCitationIndices(tr)
      return tr.docChanged ? tr : null
    },
  })
}

export const CanvasCitation = Node.create({
  name: "canvasCitation",

  inline: true,
  group: "inline",
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      citation: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-canvas-citation") ?? "",
        renderHTML: (attributes) => ({
          "data-canvas-citation": String(attributes.citation ?? ""),
        }),
      },
      index: {
        default: 0,
        parseHTML: (element) => {
          const indexAttr = element.getAttribute("data-citation-index")
          return indexAttr ? parseInt(indexAttr, 10) : 0
        },
        renderHTML: (attributes) => ({
          "data-citation-index": String(attributes.index ?? 0),
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: "span[data-canvas-citation]",
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { class: "canvas-citation" })]
  },

  addNodeView() {
    return ReactNodeViewRenderer(CitationNodeView)
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: CITATION_INPUT_REGEX,
        type: this.type,
        getAttributes: (match) => ({
          citation: match[1],
          index: 0,
        }),
      }),
    ]
  },

  addProseMirrorPlugins() {
    return [createCitationIndexerPlugin()]
  },
})
